import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// 格式化日期为"2026年5月18日"格式（本地时区，不含时间）
export function formatDate(date: string | Date | number) {
  const d = new Date(date)
  if (isNaN(d.getTime())) return '-'
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
}

// 格式化字数
export function formatWordCount(count: number) {
  return new Intl.NumberFormat('zh-CN').format(count)
}

/**
 * 统计文本字数，英文按词数，中文按字符数
 * @param text 待统计的文本
 * @returns 统计结果
 */
export function countText(text: string): { count: number; unit: string } {
  if (!text || text.trim() === '') {
    return { count: 0, unit: '字' };
  }

  const cjkPattern = /[\u4e00-\u9fff\u3040-\u30ff\uac00-\ud7af]/g;
  const cjkChars = (text.match(cjkPattern) || []).length;
  const englishPart = text.replace(cjkPattern, ' ');
  const englishWords = englishPart.trim().split(/\s+/).filter(word => word.length > 0).length;

  if (cjkChars > 0 && englishWords > 0) {
    return { count: cjkChars + englishWords, unit: '字符/词' };
  } else if (cjkChars > 0) {
    return { count: cjkChars, unit: '字' };
  } else {
    return { count: englishWords, unit: '词' };
  }
}

// 获取状态标签
export function getStatusLabel(status: string) {
  const labels: Record<string, string> = {
    pending: '待处理',
    translating: '翻译中',
    reviewing: '审校中',
    qc: '待质检',
    completed: '已完成',
    archived: '已归档',
    failed: '已失败',
  }
  return labels[status] || status
}

// 语言代码映射
export const LANGUAGE_NAMES: Record<string, string> = {
  'zh': '中文',
  'en': '英文',
  'ja': '日文',
  'ko': '韩文',
  'fr': '法文',
  'de': '德文',
  'ru': '俄文',
  'es': '西班牙文',
}

// 格式化语言对
export function formatLanguagePair(source: string, target: string) {
  const sourceName = LANGUAGE_NAMES[source] || source
  const targetName = LANGUAGE_NAMES[target] || target
  return `${sourceName} → ${targetName}`
}

/** 获取状态样式类：仅使用主题语义色，避免硬编码灰阶与调色板色值。 */
export function getStatusColor(status: string) {
  const colors: Record<string, string> = {
    pending: 'bg-secondary text-secondary-foreground',
    translating: 'bg-primary text-primary-foreground',
    reviewing: 'bg-secondary text-secondary-foreground',
    qc: 'bg-secondary text-secondary-foreground',
    completed: 'bg-secondary text-secondary-foreground',
    archived: 'bg-secondary text-secondary-foreground',
    failed: 'bg-destructive text-destructive-foreground',
  }
  return colors[status] || 'bg-secondary text-secondary-foreground'
}

// 防抖函数
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// 节流函数
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}
